import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { Button } from "components/bootstrap";
import { Icon, Spinner } from "components/common";
import { useSelectedRows, useSelectedRowsDispatch } from "common/contexts";
import { Collapsible } from "common/components";
import { Row, Col } from "security-app/components/common/FormBaseStyles";
import { DataWell } from "security-app/components/common";
import { useImportRulesFromRepo } from "security-app/hooks/useSigmaAPI";
import RuleOptionsForm from "security-app/components/SigmaRules/RuleOptionsForm";
import { formOptionsToPayload, INITIAL_RULE_OPTIONS } from "security-app/components/SigmaRules/RuleOptionsForm/utils";
import { WindowHeader, WindowBody, IconButton, FullSpinner } from "./styledComponents";
import RuleInfoTooltip from "./RuleTooltip";
const RemoveButton = styled(IconButton)`
  opacity: 0.3;

  &:hover {
    opacity: 0.9;
  }
`;
const getRuleOptionInitialValues = (rule) => ({
  id: rule.id,
  rule,
  ...INITIAL_RULE_OPTIONS
});
const formRuleToPayload = (formRule) => {
  const optionsPayload = formOptionsToPayload(formRule);
  return {
    ...optionsPayload,
    id: formRule.id
  };
};
const RuleTitle = ({ rule }) => {
  const selectedRulesDispatch = useSelectedRowsDispatch();
  const onRuleRemove = (e) => {
    e.stopPropagation();
    selectedRulesDispatch({ type: "remove", payload: [rule] });
  };
  return /* @__PURE__ */ React.createElement(Row, { $gap: "0.4rem", $align: "center" }, /* @__PURE__ */ React.createElement(RemoveButton, { $delete: true, "data-testid": "unselect-rule", onClick: onRuleRemove }, /* @__PURE__ */ React.createElement(Icon, { type: "solid", style: { cursor: "pointer" }, name: "close", title: "Remove rule" })), /* @__PURE__ */ React.createElement(RuleInfoTooltip, { rule }), rule.title);
};
function RightPanel({ importRules, setImportRules, setImportErrors, closeModal, shareRequest = void 0 }) {
  const selectedRules = useSelectedRows();
  const selectedRulesDispatch = useSelectedRowsDispatch();
  const [ruleOptionsList, setRuleOptionsList] = useState(
    []
  );
  const { importRulesFromRepo } = useImportRulesFromRepo();
  useEffect(() => {
    setRuleOptionsList((prevRules) => {
      if (prevRules.length > selectedRules.length) {
        return prevRules.filter(
          (prevRule) => selectedRules.find((sRule) => sRule.id === prevRule.id)
        );
      }
      if (prevRules.length < selectedRules.length) {
        const newRules = selectedRules.filter(
          (sRule) => !prevRules.find((prevRule) => prevRule.id === sRule.id)
        );
        return [...prevRules, ...newRules.map(getRuleOptionInitialValues)];
      }
      return prevRules;
    });
  }, [selectedRules, setRuleOptionsList]);
  useEffect(() => {
    if (importRules) {
      const rules = ruleOptionsList.map(formRuleToPayload);
      importRulesFromRepo({ rules, share_request: shareRequest }).then((data) => {
        setImportRules(false);
        if (data.failure_count > 0) setImportErrors(data.errors);
        else closeModal();
      });
    }
  }, [closeModal, importRules, importRulesFromRepo, ruleOptionsList, setImportErrors, setImportRules, shareRequest]);
  const onSubmit = useCallback(
    (values) => {
      setRuleOptionsList(
        (prevRules) => prevRules.map((pRule) => {
          if (pRule.id === values.id) return values;
          return pRule;
        })
      );
    },
    [setRuleOptionsList]
  );
  const copyOptions = (inRuleOptions) => () => {
    setRuleOptionsList(
      (prevRules) => prevRules.map((ruleOptions) => ({
        ...inRuleOptions,
        id: ruleOptions.id,
        rule: ruleOptions.rule
      }))
    );
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(WindowHeader, { style: { justifyContent: "space-between" } }, "Selected Rules: ", selectedRules.length, selectedRules.length > 0 && /* @__PURE__ */ React.createElement(IconButton, { "data-testid": "clear-selection", onClick: () => selectedRulesDispatch({ type: "clear" }) }, /* @__PURE__ */ React.createElement(Icon, { style: { cursor: "pointer" }, size: "xs", name: "close", title: "Clear selection" }))), /* @__PURE__ */ React.createElement(WindowBody, null, ruleOptionsList.map((ruleOptions) => /* @__PURE__ */ React.createElement(Collapsible, { key: `collapsible-${ruleOptions.id}`, title: /* @__PURE__ */ React.createElement(RuleTitle, { rule: ruleOptions.rule }) }, /* @__PURE__ */ React.createElement(DataWell, { style: { marginBottom: "0.5rem" } }, /* @__PURE__ */ React.createElement(Col, { $width: "100%" }, /* @__PURE__ */ React.createElement(RuleOptionsForm, { onSubmit, initialValues: ruleOptions, formId: ruleOptions.id, autoSubmit: true }), ruleOptionsList.length > 1 && /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", bsSize: "xs", type: "button", onClick: copyOptions(ruleOptions) }, "Copy these options to all"))))), importRules && /* @__PURE__ */ React.createElement(FullSpinner, null, /* @__PURE__ */ React.createElement(Spinner, { text: "Importing Sigma rules..." }))));
}
export default RightPanel;
